import styles from './HookFormTagEditor.module.scss';
import { FC, memo, useCallback, useMemo } from 'react';
import { Control, FieldValues, Path, RegisterOptions, useController } from 'react-hook-form';
import { AppDropDownWithSuggestionInput } from 'src/components/uikit/inputs/dropdown/appDropDownWithSuggestion';
import { DropdownOption } from 'src/components/uikit/inputs/dropdown/appDropdownInput';
import { AppInputError, AppInputLabel } from 'uikit/wrappers';
import { AppButton } from 'uikit/buttons';
import { AppAutoCompleteInput, AppTagInput } from 'uikit/inputs';
import { ReactComponent as AddIcon } from 'src/assets/img/common/add.svg';
import { ReactComponent as CrossIcon } from 'src/assets/img/cross.svg';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';

export type TagEditorValueItem = {
  key: string;
  values: string[];
};

type TagValidationType = 'emptyKey' | 'emptyValues';
type TagErrorType = 'key' | 'values';

type TagError = {
  message: string;
  type: TagErrorType;
};

type HookFormTagEditorProps<TFieldValues extends FieldValues = FieldValues> = {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  rules?: Exclude<RegisterOptions, 'valueAsDate' | 'setValueAs'>;
  suggestions?: { [key: string]: string[] };
  canRemoveAllTags?: boolean;
  allowFreeInputTags?: boolean;
  removeAddingTags?: boolean;
};

type SuggestionsMap = { [key: string]: string[] };

const getSuggestionKeys = (suggestions?: SuggestionsMap): string[] => Object.getOwnPropertyNames(suggestions ?? {});

const toDropdownOptions = (values: string[]): DropdownOption[] =>
  values.map((value) => ({
    key: value,
    text: value,
  }));

const hasEmptyTagValues = (tag: TagEditorValueItem): boolean =>
  (tag.values ?? []).filter((item) => !!item?.trim()).length === 0;

const hasEmptyTagKey = (tag: TagEditorValueItem): boolean => !(tag.key ?? '').trim();

export const HookFormTagEditor = <TFieldValues extends FieldValues = FieldValues>(
  props: HookFormTagEditorProps<TFieldValues>,
) => {
  const { t } = useScopedTranslation('uikit.HookFormTagEditor');

  const validateEmptyTagKey = useCallback(
    (value: TagEditorValueItem[] | null | undefined) => {
      const tags = value ?? [];
      return tags.some(hasEmptyTagKey) ? t('Validation.EmptyKey') : true;
    },
    [t],
  );

  const validateEmptyTagValues = useCallback(
    (value: TagEditorValueItem[] | null | undefined) => {
      const tags = value ?? [];
      return tags.some(hasEmptyTagValues) ? t('Validation.EmptyValues') : true;
    },
    [t],
  );

  const validators = useMemo(() => {
    const builtInValidators = {
      emptyKey: validateEmptyTagKey,
      emptyValues: validateEmptyTagValues,
    };

    if (!props.rules?.validate) return builtInValidators;

    if (typeof props.rules.validate === 'function')
      return {
        ...builtInValidators,
        external: props.rules.validate,
      };

    return {
      ...builtInValidators,
      ...props.rules.validate,
    };
  }, [props.rules?.validate, validateEmptyTagKey, validateEmptyTagValues]);

  const getTagError = useCallback(
    (tag: TagEditorValueItem, formErrorType?: string, formErrorMessage?: string): TagError | undefined => {
      if (!formErrorType || !formErrorMessage) return undefined;

      if (formErrorType === 'emptyKey' && hasEmptyTagKey(tag))
        return {
          message: formErrorMessage,
          type: 'key',
        };

      if (formErrorType === 'emptyValues' && hasEmptyTagValues(tag))
        return {
          message: formErrorMessage,
          type: 'values',
        };

      return undefined;
    },
    [],
  );

  const { field, fieldState, formState } = useController({
    control: props.control,
    name: props.name,
    rules: {
      ...props.rules,
      validate: validators,
    },
  });
  const tagsValue = useMemo(() => (field.value as TagEditorValueItem[] | null) ?? [], [field.value]);
  const isDisabled = formState.isSubmitting || formState.isSubmitSuccessful;

  const handleDeleteTag = useCallback(
    (tagIndex: number) => {
      field.onChange(tagsValue.filter((_, index) => index !== tagIndex));
    },
    [field, tagsValue],
  );

  const handleChangeTagKey = useCallback(
    (tagIndex: number, nextKey: string) => {
      field.onChange(
        tagsValue.map((tag, index) => (index === tagIndex ? { key: nextKey, values: tag.values ?? [] } : tag)),
      );
    },
    [field, tagsValue],
  );

  const handleChangeTagValues = useCallback(
    (tagIndex: number, nextValues: string[]) => {
      field.onChange(
        tagsValue.map((tag, index) =>
          index === tagIndex
            ? {
                key: tag.key ?? '',
                values: nextValues,
              }
            : tag,
        ),
      );
    },
    [field, tagsValue],
  );

  const handleAddTag = useCallback(() => {
    field.onChange([...tagsValue, { key: '', values: [] }]);
  }, [field, tagsValue]);

  return (
    <div tabIndex={0} ref={field.ref}>
      <AppInputLabel isBold={true} disabled={isDisabled} text={t('Title')} tooltip={t('Tooltip.Text')}>
        <div className={styles.tagsContainer}>
          {tagsValue.map((tagRow, index) => {
            return (
              <Tag
                key={index}
                index={index}
                tagKey={tagRow.key ?? ''}
                tagValues={tagRow.values ?? []}
                error={getTagError(
                  tagRow,
                  fieldState.error?.type as TagValidationType | undefined,
                  fieldState.error?.message,
                )}
                disabled={isDisabled}
                suggestions={props.suggestions}
                allowFreeInputTags={props.allowFreeInputTags}
                canRemove={props.canRemoveAllTags || index !== 0 || tagsValue.length > 1}
                onDelete={handleDeleteTag}
                onChangeKey={handleChangeTagKey}
                onChangeValues={handleChangeTagValues}
              />
            );
          })}
        </div>

        {!props.removeAddingTags && (
          <AppButton
            disabled={isDisabled}
            className={styles.addTagButton}
            variant={'icon-link'}
            colorSchema={'primary'}
            Icon={AddIcon}
            text={t('NewTag')}
            onClick={handleAddTag}
          />
        )}
      </AppInputLabel>
    </div>
  );
};

type HookFormTagProps = {
  index: number;
  suggestions?: { [key: string]: string[] };
  disabled?: boolean;
  canRemove?: boolean;
  allowFreeInputTags?: boolean;
  error?: TagError;
  tagKey: string;
  tagValues: string[];
  onDelete: (tagIndex: number) => void;
  onChangeKey: (tagIndex: number, nextKey: string) => void;
  onChangeValues: (tagIndex: number, values: string[]) => void;
};

const Tag: FC<HookFormTagProps> = memo(
  ({
    index,
    suggestions,
    disabled,
    canRemove,
    allowFreeInputTags,
    error,
    tagKey,
    tagValues,
    onDelete,
    onChangeKey,
    onChangeValues,
  }) => {
    const { t } = useScopedTranslation('uikit.HookFormTagEditor');

    const tagKeySuggestions = useMemo(() => getSuggestionKeys(suggestions), [suggestions]);
    const tagKeysOptions = useMemo(() => toDropdownOptions(tagKeySuggestions), [tagKeySuggestions]);
    const tagValueOptions = useMemo(() => toDropdownOptions(suggestions?.[tagKey] ?? []), [suggestions, tagKey]);
    const keyInvalid = error?.type === 'key';
    const valuesInvalid = error?.type === 'values';

    const handleTagKeyChange = useCallback(
      (newKey: string) => {
        onChangeKey(index, newKey);
      },
      [index, onChangeKey],
    );

    const handleTagValuesChange = useCallback(
      (nextValues: string[]) => onChangeValues(index, nextValues),
      [index, onChangeValues],
    );

    const handleAddTag = useCallback(
      (value: string) => {
        if (!value || tagValues.includes(value)) return;
        handleTagValuesChange([...tagValues, value]);
      },
      [handleTagValuesChange, tagValues],
    );

    const handleDeleteTag = useCallback(
      (tagIndex: number) => {
        if (tagIndex < 0) return;
        handleTagValuesChange(tagValues.filter((_, i) => i !== tagIndex));
      },
      [handleTagValuesChange, tagValues],
    );

    const handleDropdownValuesChange = useCallback(
      (options?: DropdownOption[]) => {
        handleTagValuesChange(options?.map((option) => option.key.toString()) ?? []);
      },
      [handleTagValuesChange],
    );

    return (
      <div className={styles.tagContainer} data-test-id={`${index}-tag-input`}>
        <AppInputError errors={error?.message} hideBorder>
          <div className={styles.errorWrapper}>
            <AppInputLabel disabled={disabled} text={index === 0 ? t('Key.Header') : ''}>
              {allowFreeInputTags ? (
                <AppAutoCompleteInput
                  placeholder={t('Key.Placeholder')}
                  disabled={disabled}
                  suggestions={tagKeySuggestions}
                  value={tagKey}
                  onChange={handleTagKeyChange}
                  invalid={keyInvalid}
                />
              ) : (
                <AppDropDownWithSuggestionInput
                  placeholder={t('Key.Placeholder')}
                  isRequired
                  suggestion
                  disabled={disabled}
                  options={tagKeysOptions}
                  value={tagKeysOptions.find((x) => x.key === tagKey)}
                  onChange={(newOption) => handleTagKeyChange(newOption?.key.toString() ?? '')}
                  invalid={keyInvalid}
                />
              )}
            </AppInputLabel>

            <AppInputLabel disabled={disabled} text={index === 0 ? t('Value.Header') : ''}>
              {allowFreeInputTags ? (
                <AppTagInput
                  disabled={disabled}
                  values={tagValues}
                  onAddTag={handleAddTag}
                  onDeleteTag={handleDeleteTag}
                  placeholder={t('Value.Placeholder')}
                  invalid={valuesInvalid}
                />
              ) : (
                <AppDropDownWithSuggestionInput
                  isMultiple
                  suggestion
                  disabled={disabled}
                  options={tagValueOptions}
                  value={tagValueOptions.filter((x) => tagValues.includes(x.key.toString()))}
                  onChange={handleDropdownValuesChange}
                  placeholder={t('Value.Placeholder')}
                  invalid={valuesInvalid}
                />
              )}
            </AppInputLabel>
          </div>
        </AppInputError>

        <div className={styles.deleteTag}>
          {canRemove && (
            <AppButton
              testId="delete-tag-button"
              disabled={disabled}
              variant={'icon-link'}
              Icon={CrossIcon}
              colorSchema={'basic'}
              onClick={() => onDelete(index)}
            />
          )}
        </div>
      </div>
    );
  },
);
